import { Component, Input, ViewChild, ViewContainerRef, ComponentRef, OnInit, ElementRef, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { ModalAction, ModalStackItem, MODAL_TONE_ICONS } from '@app/core/services/core-modal.service';
import { ConfigurableFocusTrap, ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { EidosUtilityIconsService } from '@app/core/services/eidos-utility-icons.service';

@Component({
  selector: 'core-modal-item',
  templateUrl: './core-modal-item.component.html',
  styleUrls: ['./core-modal-item.component.scss'],
})
export class CoreModalItemComponent implements OnInit, AfterViewInit, OnDestroy {
  private focusTrap!: ConfigurableFocusTrap;
  @Input() item!: ModalStackItem;

  @ViewChild('vc', { read: ViewContainerRef, static: true }) vc!: ViewContainerRef;
  @ViewChild('modalWrapper', { static: true }) modalWrapper!: ElementRef<HTMLElement>;
  modalStyles: Record<string,string> = {}

  private ref!: ComponentRef<any>;
  constructor(
    private elRef: ElementRef<HTMLElement>,
    private focusTrapFactory: ConfigurableFocusTrapFactory,
    protected iconService: EidosUtilityIconsService
  ) { }

  get toneIconClass(): string {
    return this.item.tone ? MODAL_TONE_ICONS[this.item.tone] : '';
  }

  ngOnInit() {
    this.modalStyles = this.getModalStyles();
    this.ref = this.vc.createComponent(this.item.component, {
      injector: this.item.injector
    });

    if (this.item.internalScroll) {
      const host = this.ref.location.nativeElement as HTMLElement;

      host.style.display = 'flex';
      host.style.flexDirection = 'column';
      host.style.flex = '1';
      host.style.minHeight = '0';
      host.style.width = '100%';
      host.style.minWidth = '0';
    }

    if (this.ref.instance && typeof this.ref.instance === 'object') {
      const data: any = {};
      if(this.item.dataBinding){
        if(this.item.dataBinding.length===1){
          data[this.item.dataBinding[0]] = this.item.data;
        } else {
          this.item
              .dataBinding
              .filter(s=>s!=='')
              .forEach(s=>{
                data[s]=this.item.data[s]
              })
        }
      } else {
        data.modalData = this.item.data;
      }
      Object.assign(this.ref.instance,
        data,
        {
        modalRef: this.item.modalRef
      });
  }
  }

  ngAfterViewInit(): void {
    const host = this.elRef.nativeElement;
    this.focusTrap = this.focusTrapFactory.create(host);
    this.setActive(this.item.isActive);
  }

  /**
   * Fullscreen host catches outside clicks (pane has pointer-events: none, so CDK backdrop is unreliable).
   */
  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent): void {
    if (!this.item?.isActive || this.item.allowInteractionBelow || !this.item.closeOnEscOrBackdropClick) {
      return;
    }
    const wrapper = this.modalWrapper?.nativeElement;
    if (wrapper?.contains(event.target as Node)) {
      return;
    }
    this.item.modalRef.close();
  }

  destroy(done: () => void) {
    if (!this.item.animation) {
      this.destroyWithoutAnimation(done);
      return;
    }

    this.destroyWithAnimation(done);
  }

  private destroyWithAnimation(done: () => void) {
    const el = this.modalWrapper.nativeElement;

    if (!el) {
      this.ref.destroy();
      done();
      return;
    }

    el.classList.add('closing');

    el.addEventListener('animationend', () => {
      this.ref.destroy();
      done();
    }, { once: true });
  }

  private destroyWithoutAnimation(done: () => void) {
    this.ref.destroy();
    done();
  }

  setActive(active: boolean) {
    const el = this.modalWrapper?.nativeElement;
    if (el) {
      el.classList.toggle('active', active);
    }

    // Block page interaction unless this modal explicitly allows it
    this.elRef.nativeElement.classList.toggle(
      'blocks-interaction',
      active && !this.item.allowInteractionBelow
    );

    if (!this.focusTrap) return;

    if (active) {
      this.focusTrap._enable();
    } else {
      this.focusTrap._disable();
    }
  }

  onCloseModalClick(value?: unknown) {
    this.item.modalRef.close(value);
  }

  isActionDisabled(action: ModalAction) {
    return typeof action.disabled === 'function' ? action.disabled() : !!action.disabled;
  }

  onActionClick(action: ModalAction) {
    if (this.isActionDisabled(action)) return;
    action.onClick?.();
    if (action.closeOnClick !== false) {
      this.item.modalRef.close(action.value);
    }
  }

  getModalStyles(): Record<string, string> {
    const d = this.item.dimensions;

    if (!d) return {};
    const dimensionObject = {
      width: this.toCssUnit(d.width),
      height: this.toCssUnit(d.height),

      minWidth: this.toCssUnit(d.minWidth),
      minHeight: this.toCssUnit(d.minHeight),

      maxWidth: this.toCssUnit(d.maxWidth),
      maxHeight: this.toCssUnit(d.maxHeight),
    };
    return Object.fromEntries(
      Object.entries(dimensionObject)
        .filter(([_, value]) => value != null)
    ) as Record<string, string>;
  }

  private toCssUnit(value?: string | number): string | undefined {
    if (value == null) return undefined;
    // 'auto' must not become an inline style: with .modal-center { inset: 0 }
    // width/height:auto stretches the panel to the fullscreen overlay.
    if (typeof value === 'string' && (value === 'auto' || value.trim() === '')) {
      return undefined;
    }

    return typeof value === 'number'
      ? `${value}px`
      : value;
  }

  ngOnDestroy(): void {
    this.focusTrap?.destroy();
  }
}
